import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useRef, useState } from "react";
import {
	CURRENT_LINE_VALUES_QUERY,
	useSaveStraightenerRecipe,
	useSetStraightenerRecipeActive,
	useSetStraightenerRecipeCurrent,
	useSetStraightenerRecipePlcCurrent,
	useUpdateStraightenerRecipe,
} from "../../straightener-recipes/hooks/useStraightenerRecipes";
import type {
	StraightenerRecipeValueRow,
	StraightenerRecipeVersion,
} from "../../straightener-recipes/models/StraightenerRecipe";
import { getStraightenerRecipeValues } from "../../straightener-recipes/repositories/StraightenerRecipesRepository";

export type PrefillMode = "current" | "copy" | "blank";

// A new recipe starts with its Min/Max blank whatever it is seeded from: the band
// is a decision for this recipe, and one inherited from the line or from another
// product would be saved without anybody choosing it. Duplicate keeps the band,
// being a copy of the same recipe.
export const withoutBand = (
	values: StraightenerRecipeValueRow[],
): StraightenerRecipeValueRow[] =>
	values.map((v) => ({ ...v, MinValue: null, MaxValue: null }));

interface Options {
	onSavedNew?: (newRecipeId: number) => void;
	onUpdated?: () => void;
}

// Single source of truth for the recipe editor's action wiring (save / update /
// duplicate / prefill / copy-from / set-current / set-plc / toggle-active) plus
// the modal + notification state. Consumed by both the Recipes manager and the
// Trend Display inline editor via RecipeEditorPanel, so the two entry points
// cannot drift in behavior.
export const useStraightenerRecipeEditor = ({
	onSavedNew,
	onUpdated,
}: Options = {}) => {
	const [notification, setNotification] = useState("");
	const [error, setError] = useState("");
	const [newRecipeValues, setNewRecipeValues] = useState<
		StraightenerRecipeValueRow[]
	>([]);
	const [recipeToDuplicate, setRecipeToDuplicate] =
		useState<StraightenerRecipeVersion | null>(null);
	const [plcConfirmId, setPlcConfirmId] = useState<number | null>(null);
	const [copyOpen, setCopyOpen] = useState(false);
	// Label of the recipe the new draft was copied from (for display).
	const [copySourceLabel, setCopySourceLabel] = useState<string | null>(null);
	const [isCopying, setIsCopying] = useState(false);
	// True while the current-line prefill is in flight.
	const [isPrefilling, setIsPrefilling] = useState(false);
	// When true, the next successful save also sets the new recipe as current.
	const setCurrentAfterSaveRef = useRef(false);
	// Bumped on every start-from choice so a slow response can't overwrite a
	// newer one (e.g. picking Blank while the line values are still loading).
	const prefillSeqRef = useRef(0);

	const queryClient = useQueryClient();

	const { saveRecipe, isSaving } = useSaveStraightenerRecipe({
		onSuccess: (newId) => {
			if (setCurrentAfterSaveRef.current) {
				setCurrentAfterSaveRef.current = false;
				setNotification("Recipe saved and set as default");
			} else {
				setNotification("Recipe saved as a new version");
			}
			onSavedNew?.(newId);
		},
		onError: (e) => setError(e.message),
	});

	const { updateRecipe, isUpdating } = useUpdateStraightenerRecipe({
		onSuccess: () => {
			setNotification("Default recipe updated");
			onUpdated?.();
		},
		onError: (e) => setError(e.message),
	});

	const { setCurrent } = useSetStraightenerRecipeCurrent({
		onSuccess: () => setNotification("Set as default"),
		onError: (e) => setError(e.message),
	});

	const { setPlcCurrent } = useSetStraightenerRecipePlcCurrent({
		onSuccess: () => setNotification("Set as PLC current"),
		onError: (e) => setError(e.message),
	});

	const { setActive } = useSetStraightenerRecipeActive({
		onSuccess: () => setNotification("Recipe active state updated"),
		onError: (e) => setError(e.message),
	});

	// Awaits the query instead of reading the cache: the editor mounts together
	// with its modal, so on the first open the request is still in flight and a
	// cache read would seed an empty form.
	const prefillFromCurrentLine = useCallback(async () => {
		const seq = ++prefillSeqRef.current;
		setIsPrefilling(true);
		try {
			const values = await queryClient.fetchQuery<StraightenerRecipeValueRow[]>(
				CURRENT_LINE_VALUES_QUERY,
			);
			if (prefillSeqRef.current !== seq) return;
			setNewRecipeValues(withoutBand(values));
			setCopySourceLabel(null);
		} catch (e) {
			if (prefillSeqRef.current === seq) setError((e as Error).message);
		} finally {
			if (prefillSeqRef.current === seq) setIsPrefilling(false);
		}
	}, [queryClient]);

	const prefill = useCallback(
		(mode: PrefillMode) => {
			if (mode === "current") void prefillFromCurrentLine();
			else if (mode === "copy") {
				prefillSeqRef.current++;
				setCopyOpen(true);
			} else {
				prefillSeqRef.current++;
				setNewRecipeValues([]);
				setCopySourceLabel(null);
			}
		},
		[prefillFromCurrentLine],
	);

	const copyFrom = useCallback(async (recipe: StraightenerRecipeVersion) => {
		const seq = ++prefillSeqRef.current;
		setIsCopying(true);
		try {
			const values = await getStraightenerRecipeValues(recipe.Id);
			if (prefillSeqRef.current !== seq) return;
			setNewRecipeValues(withoutBand(values));
			setCopySourceLabel(
				`(${recipe.ProductTypeName ?? ""}) ${recipe.ProductName} · ${
					recipe.Name ? `${recipe.Name} · ` : ""
				}v${recipe.Version}${recipe.Crew ? ` · Crew ${recipe.Crew}` : ""}`,
			);
			setCopyOpen(false);
		} catch (e) {
			if (prefillSeqRef.current === seq) setError((e as Error).message);
		} finally {
			if (prefillSeqRef.current === seq) setIsCopying(false);
		}
	}, []);

	// Save a new recipe and immediately make it the product's current one.
	// One SP call: the save + promotion happen in a single DB transaction.
	const saveRecipeAndSetCurrent = useCallback(
		(args: Parameters<typeof saveRecipe>[0]) => {
			setCurrentAfterSaveRef.current = true;
			saveRecipe({ ...args, setAsCurrent: true });
		},
		[saveRecipe],
	);

	return {
		notification,
		setNotification,
		error,
		setError,
		newRecipeValues,
		setNewRecipeValues,
		recipeToDuplicate,
		setRecipeToDuplicate,
		plcConfirmId,
		setPlcConfirmId,
		copyOpen,
		setCopyOpen,
		copySourceLabel,
		isCopying,
		isPrefilling,
		prefillFromCurrentLine,
		saveRecipe,
		saveRecipeAndSetCurrent,
		updateRecipe,
		setCurrent,
		setPlcCurrent,
		setActive,
		prefill,
		copyFrom,
		isSaving,
		isUpdating,
	};
};

export type StraightenerRecipeEditor = ReturnType<
	typeof useStraightenerRecipeEditor
>;
